<template>
  <Modal @hide="reset">
    <template #activator="{ show }">
      <slot :show="show" />
    </template>
    <template #header="{ hide }">
      <div class="flex justify-between items-center w-full">
        <div class="flex gap-2 text-blue-800 items-center">
          <EvidenceLogo class="w-7" />
          <p class="font-semibold">Evidence Guidance Database</p>
        </div>
        <button class="p-2" @click="hide">
          <XMarkIcon class="w-6 text-slate-400 hover:text-slate-600" />
        </button>
      </div>
    </template>
    <template #content>
      <div class="w-[980px] create-review-modal flex flex-col rounded-2xl relative">
        <div
          v-if="isSearching || loading.isLoading.value"
          class="overflow-hidden absolute top-0 left-0 w-full h-1"
        >
          <div class="line absolute bg-primary-500/20 h-1"></div>
          <div class="absolute bg-primary-500 h-1 inc"></div>
          <div class="absolute bg-primary-500 h-1 dec"></div>
        </div>
        <div class="px-6 mt-2 space-y-4">
          <div class="space-y-2">
            <label class="block text-sm font-medium text-slate-700"> Query </label>
            <TextInput
              v-model="rawQuery"
              placeholder="e.g. clinical investigation"
              class="w-full"
              :input-classes="'px-3 py-2 text-sm'"
              :disabled="isSearching"
              @enter="onEnter"
            >
              <template #suffix>
                <button
                  class="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-primary-500 px-3 py-2 text-xs text-white shadow-md transition hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  @click="onEnter"
                >
                  Press Enter
                  <ArrowTurnDownLeftIcon class="w-4 h-4 text-white" />
                </button>
              </template>
            </TextInput>
          </div>
        </div>
        <div class="relative">
          <div class="mx-6 my-4 bg-white rounded-lg space-y-4">
            <div class="grid grid-cols-2 gap-4">
              <DatePicker v-model="startDate" placeholder="Start Date" class="flex-1" />
              <DatePicker v-model="endDate" placeholder="End Date" class="flex-1" />
            </div>
            <div class="space-y-2">
              <div class="flex items-center justify-between">
                <h3 class="text-sm font-medium text-slate-700">Sources</h3>
                <button
                  type="button"
                  class="px-2 py-1 text-xs font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors duration-200"
                  @click="showDetailedSelection = !showDetailedSelection"
                >
                  {{ showDetailedSelection ? "Detailed" : "Simple" }}
                </button>
              </div>
              <GuidanceSourcesSelection
                v-model="selectedSources"
                :show-detailed-selection="showDetailedSelection"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #footer="{ hide }">
      <div class="p-6 flex justify-between items-center w-full">
        <div />
        <div>
          <button
            class="truncate flex flex-row gap-2 bg-blue-800 text-white items-center py-2 px-4 rounded-md text-base font-medium"
            :class="{
              '!bg-slate-100 !text-black/50': !canImport || isSearching || loading.isLoading.value,
            }"
            :disabled="!canImport || isSearching || loading.isLoading.value"
            @click="() => importGuidanceSearch().then(() => hide())"
          >
            <ImportIcon
              class="w-5 h-5 text-white"
              :class="{
                '!text-black/50': !canImport || isSearching || loading.isLoading.value,
              }"
            />
            <span> Import {{ totalResults > -1 ? totalResults : 0 }} results </span>
          </button>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import TextInput from "@app/components/Global/Inputs/TextInput.vue";
import DatePicker from "@app/components/Global/Inputs/DatePicker.vue";
import { computed, ref } from "vue";
import { SnackbarState } from "@app/types";
import useSnackbar from "@app/composables/use-snackbar";
import { injectStrict } from "@app/utils/injectStrict";
import { ReviewKey } from "@app/views/Review/use-review";
import { ImportSourceType } from "@core/domain/types/import-source-type.type";
import { ReviewLockState } from "@core/domain/types/reviewLockState.type";
import axios from "axios";
import ImportIcon from "@app/components/Icons/ImportIcon.vue";
import { XMarkIcon, ArrowTurnDownLeftIcon } from "@heroicons/vue/24/outline";
import Modal from "@app/components/Global/Modal/Modal.vue";
import { GuidanceApiClientKey, GuidanceSourcesKey } from "@app/injectionKeys";
import { Review } from "@core/domain/models/review.model";
import { GuidanceMetadata } from "@core/domain/models/reviewItemMetadata/guidancMetadata";
import { Prettify } from "@app/utils/prettifyType";
import { ReviewItemType } from "@core/domain/types/reviewItemType.type";
import useLoading from "@app/composables/use-loading";
import EvidenceLogo from "@app/components/Icons/EvidenceLogo.vue";
import { watchDebounced } from "@vueuse/core";
import GuidanceSourcesSelection from "@app/components/GuidanceSourcesSelection.vue";

const guidanceSources = injectStrict(GuidanceSourcesKey);
const guidanceApiClient = injectStrict(GuidanceApiClientKey);
const review = injectStrict(ReviewKey);
const snackbar = useSnackbar();
const loading = useLoading();
const showDetailedSelection = ref(false);

const results = ref<GuidanceMetadata[]>([]);
const currentPage = ref(1);
const totalPages = ref(1);
const limit = ref(12);
const totalResults = ref(-1);

const isSearching = ref(false);
const rawQuery = ref("");
const startDate = ref("");
const endDate = ref("");
const selectedSources = ref<string[]>(guidanceSources.map((s) => s.id));

const canImport = computed(() => {
  return (
    review.entity.value?.plan?.lockState === ReviewLockState.UNLOCKED &&
    selectedSources.value.length > 0
  );
});

async function importGuidanceSearch() {
  loading.start();
  try {
    const { results: searchResults } = await guidanceApiClient.search({
      query: rawQuery.value.trim() || undefined,
      sources: selectedSources.value,
      filters: {
        startDate: startDate.value,
        endDate: endDate.value,
      },
      pagination: { limit: 5000, offset: 0 },
    });

    const dateFilter = buildDateFilterLabel();

    const guidanceBySource = new Map<string, GuidanceMetadata[]>();
    selectedSources.value.forEach((sourceId) => {
      guidanceBySource.set(sourceId, []);
    });
    searchResults.forEach((item) => {
      const sourceId = item.source;
      if (sourceId && guidanceBySource.has(sourceId)) {
        guidanceBySource.get(sourceId)!.push(item);
      }
    });

    for (const [sourceId, items] of guidanceBySource) {
      const guidanceSource = guidanceSources.find((s) => s.id === sourceId);
      if (!guidanceSource) continue;

      if (!sourceExistsInPlan(sourceId, review.entity.value)) {
        await review.addImportSourceToPlan({
          name: guidanceSource.name,
          type: ImportSourceType.FIELD_SAFETY_NOTICES,
          url: guidanceSource.url ?? "",
          id: guidanceSource.id,
          description: guidanceSource.description,
        });
      }

      await review.importSearch({
        query: rawQuery.value.trim() || undefined,
        date: new Date().toISOString(),
        importSourceId: sourceId,
        filters: dateFilter || undefined,
        items: items.map(
          (item) =>
            ({
              ...item,
              type: ReviewItemType.Guidance,
            }) as Prettify<GuidanceMetadata & { type: ReviewItemType.Guidance }>,
        ),
      });
    }

    snackbar.success("Success");
    reset();
  } catch (error) {
    if (axios.isAxiosError(error) && error.response && error.response.status === 500) {
      snackbar.show(SnackbarState.ERROR, "Internal Server Error. Please try again.");
    } else {
      snackbar.show(SnackbarState.ERROR, "Failed to import guidance.");
    }
    console.error("Error importing guidance:", error);
  } finally {
    loading.stop();
  }
}

function sourceExistsInPlan(importSourceId: string, rev: Review): boolean {
  const enabledSources = rev.plan?.importPlan.importSources || [];
  return enabledSources.some((source) => source.id === importSourceId);
}

function buildDateFilterLabel(): string {
  if (startDate.value && endDate.value) {
    return `Date: from ${startDate.value} to ${endDate.value}`;
  } else if (startDate.value) {
    return `Date: from ${startDate.value}`;
  } else if (endDate.value) {
    return `Date: to ${endDate.value}`;
  }
  return "";
}

async function search(params: { limit: number; offset: number }) {
  isSearching.value = true;
  try {
    if (selectedSources.value.length === 0) {
      results.value = [];
      totalResults.value = 0;
      isSearching.value = false;
      return;
    }

    const { results: guidanceRecords, totalFound } = await guidanceApiClient.search({
      pagination: params,
      query: rawQuery.value.trim() || undefined,
      sources: selectedSources.value,
      filters: {
        startDate: startDate.value,
        endDate: endDate.value,
      },
    });

    if (guidanceRecords.length) {
      results.value = guidanceRecords;
      totalResults.value = totalFound;
      totalPages.value = Math.ceil(totalResults.value / limit.value);
    } else {
      results.value = [];
      totalResults.value = 0;
      totalPages.value = 1;
    }
  } catch (error) {
    if (axios.isAxiosError(error) && error.response && error.response.status === 500) {
      snackbar.show(SnackbarState.ERROR, "Internal Server Error. Please try again.");
    }
    console.error("Error fetching guidance:", error);
    results.value = [];
    totalResults.value = 0;
    totalPages.value = 1;
  } finally {
    isSearching.value = false;
  }
}

function onEnter() {
  search({ limit: limit.value, offset: (currentPage.value - 1) * limit.value });
}

watchDebounced(
  () => [
    rawQuery.value,
    startDate.value,
    endDate.value,
    selectedSources.value.slice().sort().join(","),
  ],
  () => {
    currentPage.value = 1;
    search({ limit: limit.value, offset: 0 });
  },
  { debounce: 800 },
);

function reset() {
  results.value = [];
  currentPage.value = 1;
  totalPages.value = 1;
  totalResults.value = -1;
  rawQuery.value = "";
  startDate.value = "";
  endDate.value = "";
  selectedSources.value = guidanceSources.map((s) => s.id);
}
</script>
